import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import DateTimeField from '@app/components/shared/reportModal/DateTimeField';
import { Incident, localToIso, toLocalInput } from './sources/incidents';

/** Saying an incident is waiting on somebody else. The hour is asked, since a wait is written up after it starts. */
const IncidentPauseDialog = ({
  incident,
  ending,
  onConfirm,
  onCancel,
}: {
  incident: Incident | null;
  /** Whether this is the end of a wait rather than the start of one. */
  ending: boolean;
  /** The stretch as local `datetime-local` values; `endLocal` is empty while the wait is still going. */
  onConfirm: (atLocal: string, endLocal: string, reason: string) => void;
  onCancel: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [at, setAt] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');

  // A fresh answer per incident, so both fields go back to their defaults.
  useEffect(() => {
    if (!incident) return;
    // Ending one is the exception: Resume is pressed as the answer comes back, so now really is now.
    setAt(ending ? toLocalInput(new Date().toISOString()) : '');
    setUntil('');
    setReason('');
  }, [incident?.id, ending]);

  // Only what the two fields can judge: the server's "error while validating body" names neither field nor reason.
  const inFuture = (value: string): boolean => !!value && Date.parse(localToIso(value)) > Date.now();
  const startedInFuture = inFuture(at);
  const endedInFuture = !ending && inFuture(until);
  const backwards = !ending && !!until && Date.parse(localToIso(until)) <= Date.parse(localToIso(at));

  return (
    <Dialog open={!!incident} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(ending ? 'incidents.resumeTitle' : 'incidents.pauseTitle')}</DialogTitle>
          <DialogDescription>{incident?.title}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <DateTimeField
            id="incident-paused-at"
            label={t(ending ? 'incidents.pauseEndedAt' : 'incidents.pauseStartedAt')}
            value={at}
            onChange={setAt}
            defaultDay={new Date()}
            invalid={startedInFuture}
            // Nobody waited on an answer that has not been asked for yet.
            disabledDays={{ after: new Date() }}
          />
          {startedInFuture ? (
            <p className="text-[11px] text-destructive">{t('incidents.error.pauseInFuture')}</p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              {t(ending ? 'incidents.resumeMeaning' : 'incidents.pauseMeaning')}
            </p>
          )}
        </div>

        {/* Only when the wait is being opened: one that is already running has its start on the record,
            and this dialog is then asking for the end and nothing else. */}
        {!ending && (
          <div className="flex flex-col gap-1.5">
            <DateTimeField
              id="incident-paused-until"
              label={t('incidents.pauseUntil')}
              value={until}
              onChange={setUntil}
              invalid={backwards || endedInFuture}
              // The day standing there is not an answer: the field counts as blank until the hour is typed.
              defaultDay={new Date()}
              disabledDays={{ after: new Date() }}
            />
            {(() => {
              if (endedInFuture)
                return <p className="text-[11px] text-destructive">{t('incidents.error.pauseInFuture')}</p>;
              if (backwards)
                return <p className="text-[11px] text-destructive">{t('incidents.error.pauseEndsBeforeStart')}</p>;
              return <p className="text-[11px] text-muted-foreground">{t('incidents.pauseStillWaiting')}</p>;
            })()}
          </div>
        )}

        {!ending && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="incident-pause-reason">{t('incidents.pauseReason')}</Label>
            <Input
              id="incident-pause-reason"
              value={reason}
              placeholder={t('incidents.pauseReasonPlaceholder')}
              onChange={event => setReason(event.target.value)}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button
            disabled={!at || backwards || startedInFuture || endedInFuture}
            onClick={() => onConfirm(at, until, reason.trim())}
          >
            {t(ending ? 'incidents.resumeConfirm' : 'incidents.pauseConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IncidentPauseDialog;
